import { RESIDENTIAL_STATES } from "../config";

export const WEB_SIGNUP_STATE_PARAM = "su_state";
export const WEB_SIGNUP_PREFERENCE_PREFIX = "wlpk_";
export const WEB_SIGNUP_PREFERENCE_MAX_PAIRS = 20;

/** Names signup already writes on the marketing profile. Campaign answers cannot replace them. */
export const SIGNUP_PREFERENCE_RESERVED_PROPERTY_NAMES = [
  "email",
  "emailAddress",
  "phone_number",
  "phoneNumber",
  "phoneNumberAlt",
  "phoneNumberVerified",
  "external_id",
  "first_name",
  "firstName",
  "last_name",
  "lastName",
  "location",
  "userId",
  "dateOfBirth",
  "state",
  "suburb",
  "subscription",
  "env",
  "entryPoints",
  "rewardPoints",
  "isActive",
  "source",
  "campaign",
  "content",
  "medium",
  "wlLanding",
  "ABTestClassification",
  "createdDt",
  "updatedDt",
  "downloadedApp",
  "downloadedAppText",
  "isDeleted",
  "isDeletedText",
  "isCancelled",
  "isCancelledText",
  "referredByMemberId",
  "stripeId",
] as const;

const reservedPropertyNames = new Set(
  SIGNUP_PREFERENCE_RESERVED_PROPERTY_NAMES.map((name) => name.toLowerCase()),
);

const toSearchParams = (search: string): URLSearchParams => {
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
};

export const resolveSignupStateFromSearch = (search: string): string | null => {
  try {
    const raw = (toSearchParams(search).get(WEB_SIGNUP_STATE_PARAM) || "").trim();
    if (!raw) {
      return null;
    }
    const matched = RESIDENTIAL_STATES.find(
      (option) => option.value.toLowerCase() === raw.toLowerCase(),
    );
    return matched ? matched.value : null;
  } catch {
    return null;
  }
};

const normalizePreferenceEntries = (
  entries: Array<[string, string]>,
): Record<string, string> | null => {
  const properties: Record<string, string> = {};

  for (const [rawName, rawValue] of entries) {
    const name = rawName.trim();
    const value = rawValue.trim();
    if (!name || !value) {
      continue;
    }
    if (reservedPropertyNames.has(name.toLowerCase())) {
      continue;
    }
    const isExisting = Object.prototype.hasOwnProperty.call(properties, name);
    const atCap =
      Object.keys(properties).length >= WEB_SIGNUP_PREFERENCE_MAX_PAIRS;
    if (!isExisting && atCap) {
      continue;
    }
    properties[name] = value;
  }

  return Object.keys(properties).length > 0 ? properties : null;
};

export const parseSignupPreferenceProperties = (
  search: string,
): Record<string, string> | null => {
  try {
    const entries: Array<[string, string]> = [];
    toSearchParams(search).forEach((value, key) => {
      if (!key.startsWith(WEB_SIGNUP_PREFERENCE_PREFIX)) {
        return;
      }
      entries.push([key.slice(WEB_SIGNUP_PREFERENCE_PREFIX.length), value]);
    });
    return normalizePreferenceEntries(entries);
  } catch {
    return null;
  }
};

export const readSignupPreferencePropertiesFromLocation = (): Record<
  string,
  string
> | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return parseSignupPreferenceProperties(window.location.search);
};

export const readSignupStateFromLocation = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return resolveSignupStateFromSearch(window.location.search);
};
